// ============================================================================
// IVision Marketing — Business Settings (Full Rewrite)
// Drop-in replacement for: BusinessSettings.js
// Route: /app/business-settings
// API: GET /api/business-settings, PUT /api/business-settings, PUT /api/business-settings/hours
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import axios from './lib/axiosClient';
import { useTenant } from './contexts/TenantContext';

// ── Industry Configuration ──────────────────────────────────────────────────
const INDUSTRIES = [
  { value: 'spa', label: 'Spa / Massage', icon: '💆', color: '#8B6F47', desc: 'Massage therapy, facials, body treatments' },
  { value: 'barbershop', label: 'Barbershop / Salon', icon: '💈', color: '#C4943E', desc: 'Haircuts, styling, grooming' },
  { value: 'nails', label: 'Nail Salon', icon: '💅', color: '#E8788A', desc: 'Manicures, pedicures, nail art' },
  { value: 'pet', label: 'Pet Grooming', icon: '🐾', color: '#D4A24E', desc: 'Pet baths, grooming, boarding' },
  { value: 'auto', label: 'Auto / Motorcycle Repair', icon: '🔧', color: '#DC2626', desc: 'Repairs, maintenance, inspections' },
  { value: 'smokeshop', label: 'Smoke Shop / Retail', icon: '🏪', color: '#7C3AED', desc: 'Retail, inventory, POS' },
  { value: 'fitness', label: 'Fitness / Gym', icon: '🏋️', color: '#EF4444', desc: 'Classes, memberships, training' },
  { value: 'medspa', label: 'Med Spa', icon: '🏥', color: '#0891B2', desc: 'Aesthetic treatments, injectables' },
  { value: 'other', label: 'Other Service Business', icon: '🏢', color: '#6B7280', desc: 'Custom configuration' },
];

const INDUSTRY_FEATURES = {
  spa:        { bookings: true, workOrders: false, inventory: false, tips: true, loyalty: true, classes: false, memberships: true },
  barbershop: { bookings: true, workOrders: false, inventory: true,  tips: true, loyalty: true, classes: false, memberships: false },
  nails:      { bookings: true, workOrders: false, inventory: true,  tips: true, loyalty: true, classes: false, memberships: false },
  pet:        { bookings: true, workOrders: false, inventory: true,  tips: true, loyalty: true, classes: false, memberships: false },
  auto:       { bookings: false, workOrders: true, inventory: true,  tips: false, loyalty: false, classes: false, memberships: false },
  smokeshop:  { bookings: false, workOrders: false, inventory: true, tips: false, loyalty: true, classes: false, memberships: false },
  fitness:    { bookings: true, workOrders: false, inventory: false, tips: false, loyalty: true, classes: true, memberships: true },
  medspa:     { bookings: true, workOrders: false, inventory: true,  tips: true, loyalty: true, classes: false, memberships: true },
  other:      { bookings: true, workOrders: true, inventory: true,   tips: true, loyalty: true, classes: true, memberships: true },
};

const INDUSTRY_TERMS = {
  spa:        { customer: 'Client', service: 'Treatment', booking: 'Appointment', staff: 'Therapist' },
  barbershop: { customer: 'Client', service: 'Service', booking: 'Appointment', staff: 'Barber' },
  nails:      { customer: 'Client', service: 'Service', booking: 'Appointment', staff: 'Technician' },
  pet:        { customer: 'Pet Parent', service: 'Service', booking: 'Appointment', staff: 'Groomer' },
  auto:       { customer: 'Customer', service: 'Service', booking: 'Work Order', staff: 'Mechanic' },
  smokeshop:  { customer: 'Customer', service: 'Product', booking: 'Order', staff: 'Associate' },
  fitness:    { customer: 'Member', service: 'Class', booking: 'Session', staff: 'Trainer' },
  medspa:     { customer: 'Patient', service: 'Procedure', booking: 'Appointment', staff: 'Provider' },
  other:      { customer: 'Customer', service: 'Service', booking: 'Booking', staff: 'Staff' },
};

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
];

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ── Main Component ──────────────────────────────────────────────────────────
function BusinessSettings() {
  const { businessType: tenantType } = useTenant();
  const [tab, setTab] = useState('general');
  const [settings, setSettings] = useState({});
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [stripeStatus, setStripeStatus] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { loadSettings(); }, []);

  useEffect(() => {
    if (tab === 'payments') loadStripeStatus();
  }, [tab]);

  const loadSettings = async () => {
    try {
      const res = await axios.get('/api/business-settings');
      if (res.data.success) {
        setSettings(res.data.settings || {});
        if (res.data.hours?.length > 0) {
          setHours(res.data.hours);
        } else {
          setHours(DAYS.map((_, i) => ({
            day_of_week: i, open_time: '09:00', close_time: '21:00', is_closed: i === 0
          })));
        }
      }
    } catch (err) { console.error('Failed to load settings:', err); }
    setLoading(false);
  };

  const loadStripeStatus = async () => {
    try {
      const res = await axios.get('/api/stripe/status');
      if (res.data) setStripeStatus(res.data);
    } catch { setStripeStatus({ connected: false }); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put('/api/business-settings', settings);
      flash('Settings saved!');
    } catch (err) {
      flash('Error saving. Please try again.');
    }
    setSaving(false);
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await axios.put('/api/business-settings/hours', { hours });
      flash('Hours saved!');
    } catch (err) {
      flash('Error saving hours.');
    }
    setSaving(false);
  };

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const u = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const updateHour = (dayIndex, field, val) => {
    setHours(prev => prev.map(h => h.day_of_week === dayIndex ? { ...h, [field]: val } : h));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { flash('Logo must be under 2MB'); return; }
    const formData = new FormData();
    formData.append('logo', file);
    setSaving(true);
    try {
      const res = await axios.post('/api/business-settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.url) u('logo_url', res.data.url);
      flash('Logo uploaded!');
    } catch { flash('Upload failed — endpoint may not be configured yet'); }
    setSaving(false);
  };

  const handleStripeConnect = async () => {
    setSaving(true);
    try {
      const res = await axios.post('/api/stripe/connect');
      if (res.data.url) window.location.href = res.data.url;
    } catch { flash('Stripe connect not configured yet'); }
    setSaving(false);
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('Passwords do not match'); return;
    }
    if (passwordForm.new.length < 8) {
      setPasswordError('Password must be at least 8 characters'); return;
    }
    setSaving(true);
    try {
      await axios.put('/api/user/password', { currentPassword: passwordForm.current, newPassword: passwordForm.new });
      setPasswordForm({ current: '', new: '', confirm: '' });
      flash('Password updated!');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to update password');
    }
    setSaving(false);
  };

  // Use tenant context business type, fallback to settings
  const bt = tenantType || settings.business_type || 'other';
  const industryColor = INDUSTRIES.find(i => i.value === bt)?.color || '#ff6b2b';
  const terms = INDUSTRY_TERMS[bt] || INDUSTRY_TERMS.other;
  const features = INDUSTRY_FEATURES[bt] || INDUSTRY_FEATURES.other;

  const TABS = [
    { key: 'general', label: 'General', icon: '🏢' },
    { key: 'type', label: 'Business Type', icon: '🏷️' },
    { key: 'hours', label: 'Hours', icon: '🕐' },
    { key: 'booking', label: 'Booking', icon: '📅' },
    { key: 'branding', label: 'Branding', icon: '🎨' },
    { key: 'notifications', label: 'Notifications', icon: '🔔' },
    { key: 'payments', label: 'Payments', icon: '💳' },
    { key: 'account', label: 'Account', icon: '🔒' },
  ];

  if (loading) return (
    <div style={S.loadingWrap}>
      <div style={S.spinner} />
      <p style={{ color: '#94a3b8', marginTop: 16 }}>Loading settings...</p>
    </div>
  );

  return (
    <div style={S.page}>
      {toast && (
        <div style={S.toast}>
          <span>{toast.includes('Error') || toast.includes('failed') ? '⚠️' : '✅'}</span>
          <span>{toast}</span>
        </div>
      )}

      <div style={S.header}>
        <h1 style={S.title}>Settings</h1>
        <p style={S.subtitle}>Manage your business configuration</p>
      </div>

      <div style={S.layout}>
        {/* Tab Sidebar */}
        <nav style={S.sidebar}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ ...S.sideTab, ...(tab === t.key ? { background: '#1e293b', color: '#f8fafc', borderLeft: `3px solid ${industryColor}` } : {}) }}>
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <div style={S.content}>

          {/* ── GENERAL ─────────────────────────────────────────────── */}
          {tab === 'general' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Business Information</h2>
                <p style={S.cardDesc}>Your core business details. This info appears on invoices, booking pages, and customer communications.</p>
              </div>
              <div style={S.divider} />
              <div style={S.grid2}>
                <InputField label="Business Name" value={settings.business_name} onChange={v => u('business_name', v)} placeholder="e.g. Angel Massage & Spa" />
                <InputField label="Owner / Manager Name" value={settings.owner_name} onChange={v => u('owner_name', v)} placeholder="Full name" />
                <InputField label="Email" value={settings.email} onChange={v => u('email', v)} type="email" placeholder="hello@yourbusiness.com" />
                <InputField label="Phone" value={settings.phone} onChange={v => u('phone', v)} type="tel" placeholder="(702) 555-0100" />
              </div>
              <div style={S.grid2}>
                <InputField label="Address" value={settings.address} onChange={v => u('address', v)} placeholder="Street address" span2 />
              </div>
              <div style={S.grid3}>
                <InputField label="City" value={settings.city} onChange={v => u('city', v)} placeholder="Las Vegas" />
                <InputField label="State" value={settings.state} onChange={v => u('state', v)} placeholder="NV" />
                <InputField label="ZIP Code" value={settings.zip} onChange={v => u('zip', v)} placeholder="89101" />
              </div>
              <div style={S.grid2}>
                <SelectField label="Timezone" value={settings.timezone || 'America/Los_Angeles'} onChange={v => u('timezone', v)} options={TIMEZONES} />
                <InputField label="Website" value={settings.website} onChange={v => u('website', v)} placeholder="https://yourbusiness.com" />
              </div>
              <div style={S.fieldGroup}>
                <label style={S.label}>Cancellation Policy</label>
                <textarea style={S.textarea} rows={3} value={settings.cancellation_policy || ''}
                  onChange={e => u('cancellation_policy', e.target.value)}
                  placeholder="e.g. Cancellations must be made at least 24 hours in advance..." />
              </div>
              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveSettings} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* ── BUSINESS TYPE ───────────────────────────────────────── */}
          {tab === 'type' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Business Type</h2>
                <p style={S.cardDesc}>Selecting your industry customizes the entire platform — terminology, enabled features, sidebar navigation, default colors, and more.</p>
              </div>
              <div style={S.divider} />

              <div style={S.industryGrid}>
                {INDUSTRIES.map(ind => {
                  const selected = (settings.business_type || bt) === ind.value;
                  return (
                    <button key={ind.value} onClick={() => u('business_type', ind.value)}
                      style={{ ...S.industryCard, borderColor: selected ? ind.color : '#2a2f3a', background: selected ? `${ind.color}10` : '#13161c', boxShadow: selected ? `0 0 0 1px ${ind.color}, 0 4px 20px ${ind.color}20` : 'none' }}>
                      <div style={{ fontSize: 32 }}>{ind.icon}</div>
                      <div style={{ fontWeight: 600, color: selected ? '#f1f5f9' : '#94a3b8', marginTop: 6 }}>{ind.label}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.3 }}>{ind.desc}</div>
                      {selected && <div style={{ ...S.selectedBadge, background: ind.color }}>✓ Selected</div>}
                    </button>
                  );
                })}
              </div>

              {(settings.business_type || bt) && (
                <>
                  <div style={{ ...S.previewSection, marginTop: 28 }}>
                    <h3 style={S.previewTitle}><span style={{ color: industryColor }}>●</span> Enabled Features</h3>
                    <div style={S.featureGrid}>
                      {Object.entries(INDUSTRY_FEATURES[settings.business_type || bt] || INDUSTRY_FEATURES.other).map(([key, enabled]) => (
                        <div key={key} style={{ ...S.featureChip, opacity: enabled ? 1 : 0.35 }}>
                          <span>{enabled ? '✅' : '⬜'}</span>
                          <span style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ ...S.previewSection, marginTop: 20 }}>
                    <h3 style={S.previewTitle}><span style={{ color: industryColor }}>●</span> Platform Terminology</h3>
                    <div style={S.termGrid}>
                      {Object.entries(INDUSTRY_TERMS[settings.business_type || bt] || INDUSTRY_TERMS.other).map(([key, val]) => (
                        <div key={key} style={S.termItem}>
                          <span style={S.termKey}>{key}</span>
                          <span style={S.termArrow}>→</span>
                          <span style={S.termVal}>{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveSettings} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Business Type'}
                </button>
              </div>
            </div>
          )}

          {/* ── HOURS ───────────────────────────────────────────────── */}
          {tab === 'hours' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Operating Hours</h2>
                <p style={S.cardDesc}>Set when your business is open. These appear on your booking page and public website.</p>
              </div>
              <div style={S.divider} />
              <div>
                {DAYS.map((day, i) => {
                  const h = hours.find(x => x.day_of_week === i) || {};
                  const closed = h.is_closed;
                  return (
                    <div key={i} style={{ ...S.hourRow, opacity: closed ? 0.5 : 1 }}>
                      <div style={S.hourDay}>
                        <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 14 }}>{day}</div>
                        <div style={{ fontSize: 11, color: closed ? '#ef4444' : '#4ade80', marginTop: 2 }}>{closed ? 'Closed' : 'Open'}</div>
                      </div>
                      <div style={S.hourTimes}>
                        <input type="time" value={h.open_time || '09:00'} onChange={e => updateHour(i, 'open_time', e.target.value)}
                          style={{ ...S.timeInput, opacity: closed ? 0.3 : 1 }} disabled={closed} />
                        <span style={{ color: '#475569', fontSize: 13 }}>to</span>
                        <input type="time" value={h.close_time || '21:00'} onChange={e => updateHour(i, 'close_time', e.target.value)}
                          style={{ ...S.timeInput, opacity: closed ? 0.3 : 1 }} disabled={closed} />
                      </div>
                      <button onClick={() => updateHour(i, 'is_closed', !closed)}
                        style={{ ...S.toggleBtn, background: closed ? '#1e293b' : '#065f46', color: closed ? '#94a3b8' : '#6ee7b7' }}>
                        {closed ? 'Set Open' : 'Set Closed'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveHours} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Hours'}
                </button>
              </div>
            </div>
          )}

          {/* ── BOOKING RULES ───────────────────────────────────────── */}
          {tab === 'booking' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Booking Rules</h2>
                <p style={S.cardDesc}>Control how {terms.customer.toLowerCase()}s can book {terms.booking.toLowerCase()}s through your platform.</p>
              </div>
              <div style={S.divider} />
              <div style={S.grid2}>
                <InputField label={`Buffer Between ${terms.booking}s (minutes)`} value={settings.booking_buffer_minutes} onChange={v => u('booking_buffer_minutes', v)} type="number" placeholder="15" />
                <InputField label="Max Advance Booking (days)" value={settings.max_advance_days} onChange={v => u('max_advance_days', v)} type="number" placeholder="30" />
                <InputField label="Min Advance Booking (hours)" value={settings.min_advance_hours} onChange={v => u('min_advance_hours', v)} type="number" placeholder="1" />
                <InputField label="Cancellation Window (hours before)" value={settings.cancellation_hours} onChange={v => u('cancellation_hours', v)} type="number" placeholder="24" />
              </div>
              <div style={S.switchGroup}>
                <SwitchRow label={`Allow Online ${terms.booking}s`} desc={`${terms.customer}s can book directly from your public page`} checked={settings.allow_online_booking !== false} onChange={v => u('allow_online_booking', v)} />
                <SwitchRow label="Require Deposit" desc={`Collect a deposit when ${terms.customer.toLowerCase()}s book online`} checked={settings.require_deposit === true} onChange={v => u('require_deposit', v)} />
                <SwitchRow label="Auto-Confirm Bookings" desc="Automatically confirm new bookings (skip pending status)" checked={settings.auto_confirm !== false} onChange={v => u('auto_confirm', v)} />
                <SwitchRow label={`Allow ${terms.customer} Cancellation`} desc={`${terms.customer}s can cancel their own ${terms.booking.toLowerCase()}s online`} checked={settings.allow_customer_cancel !== false} onChange={v => u('allow_customer_cancel', v)} />
                <SwitchRow label="Double Booking Protection" desc={`Prevent overlapping ${terms.booking.toLowerCase()}s for the same ${terms.staff.toLowerCase()}`} checked={settings.prevent_double_book !== false} onChange={v => u('prevent_double_book', v)} />
              </div>
              {settings.require_deposit && (
                <div style={{ ...S.grid2, marginTop: 16 }}>
                  <InputField label="Deposit Amount ($)" value={settings.deposit_amount} onChange={v => u('deposit_amount', v)} type="number" placeholder="25" />
                  <SelectField label="Deposit Type" value={settings.deposit_type || 'flat'} onChange={v => u('deposit_type', v)}
                    options={[{ value: 'flat', label: 'Flat Amount ($)' }, { value: 'percent', label: 'Percentage (%)' }]} />
                </div>
              )}
              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Booking Rules'}</button>
              </div>
            </div>
          )}

          {/* ── BRANDING ────────────────────────────────────────────── */}
          {tab === 'branding' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Branding & Appearance</h2>
                <p style={S.cardDesc}>Customize the look and feel of your booking page, emails, and customer portal.</p>
              </div>
              <div style={S.divider} />

              {/* Logo */}
              <div style={S.logoSection}>
                <div style={S.logoPreview}>
                  {settings.logo_url ? (
                    <img src={settings.logo_url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ color: '#475569', fontSize: 14 }}>No logo</div>
                  )}
                </div>
                <div>
                  <label style={S.label}>Business Logo</label>
                  <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 12px' }}>PNG or JPG, max 2MB. Appears on booking page and emails.</p>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  <button style={S.uploadBtn} onClick={() => fileInputRef.current?.click()}>📤 Upload Logo</button>
                  {settings.logo_url && (
                    <button style={{ ...S.uploadBtn, marginLeft: 8, color: '#ef4444' }} onClick={() => u('logo_url', '')}>Remove</button>
                  )}
                </div>
              </div>

              {/* Colors */}
              <div style={{ ...S.grid2, marginTop: 24 }}>
                <ColorField label="Primary Color" value={settings.primary_color || industryColor} onChange={v => u('primary_color', v)} />
                <ColorField label="Secondary Color" value={settings.secondary_color || '#ffab40'} onChange={v => u('secondary_color', v)} />
              </div>

              {/* Live Preview */}
              <div style={{ marginTop: 28 }}>
                <label style={{ ...S.label, marginBottom: 12 }}>Live Preview</label>
                <div style={{
                  background: `linear-gradient(135deg, ${settings.primary_color || industryColor}, ${settings.secondary_color || '#ffab40'})`,
                  borderRadius: 12, padding: 28, color: '#fff', position: 'relative', overflow: 'hidden',
                }}>
                  <div style={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                  <div style={{ position: 'absolute', bottom: -30, left: -30, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, opacity: 0.85, marginBottom: 6 }}>Your Booking Page</div>
                    <div style={{ fontSize: 22, fontWeight: 700 }}>{settings.business_name || 'Your Business Name'}</div>
                    <div style={{ fontSize: 14, opacity: 0.9, marginTop: 4 }}>Book your next {terms.booking.toLowerCase()} today</div>
                    <div style={{ marginTop: 18, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', borderRadius: 8, padding: '10px 24px', display: 'inline-block', fontWeight: 600, fontSize: 14 }}>
                      Book Now →
                    </div>
                  </div>
                </div>
              </div>

              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Branding'}</button>
              </div>
            </div>
          )}

          {/* ── NOTIFICATIONS ───────────────────────────────────────── */}
          {tab === 'notifications' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Notifications</h2>
                <p style={S.cardDesc}>Configure how and when your {terms.customer.toLowerCase()}s receive reminders and updates.</p>
              </div>
              <div style={S.divider} />

              <div style={S.sectionLabel}>SMS Notifications</div>
              <div style={S.switchGroup}>
                <SwitchRow label={`${terms.booking} Confirmation SMS`} desc={`Send SMS when a ${terms.booking.toLowerCase()} is confirmed`} checked={settings.sms_booking_confirm !== false} onChange={v => u('sms_booking_confirm', v)} />
                <SwitchRow label="Reminder SMS" desc={`Send a reminder before the scheduled ${terms.booking.toLowerCase()}`} checked={settings.sms_reminder !== false} onChange={v => u('sms_reminder', v)} />
                <SwitchRow label="Cancellation SMS" desc={`Notify ${terms.customer.toLowerCase()} when their ${terms.booking.toLowerCase()} is cancelled`} checked={settings.sms_cancellation !== false} onChange={v => u('sms_cancellation', v)} />
                {features.workOrders && (
                  <SwitchRow label="Work Order Status SMS" desc="Send SMS when work order status changes" checked={settings.sms_work_order !== false} onChange={v => u('sms_work_order', v)} />
                )}
              </div>

              {settings.sms_reminder !== false && (
                <div style={{ marginTop: 16 }}>
                  <InputField label="Reminder Lead Time (hours before)" value={settings.reminder_hours_before || 24} onChange={v => u('reminder_hours_before', v)} type="number" placeholder="24" />
                </div>
              )}

              <div style={{ ...S.sectionLabel, marginTop: 28 }}>Email Notifications</div>
              <div style={S.switchGroup}>
                <SwitchRow label={`${terms.booking} Confirmation Email`} desc={`Send a detailed email with ${terms.booking.toLowerCase()} info`} checked={settings.email_booking_confirm !== false} onChange={v => u('email_booking_confirm', v)} />
                <SwitchRow label="Reminder Email" desc="Send email reminder in addition to SMS" checked={settings.email_reminder === true} onChange={v => u('email_reminder', v)} />
                <SwitchRow label="Review Request Email" desc={`Ask ${terms.customer.toLowerCase()}s to leave a review after their visit`} checked={settings.email_review_request === true} onChange={v => u('email_review_request', v)} />
                <SwitchRow label="Book Again Email" desc={`Re-engage ${terms.customer.toLowerCase()}s who haven't visited recently`} checked={settings.email_book_again === true} onChange={v => u('email_book_again', v)} />
              </div>

              {settings.email_book_again && (
                <div style={{ marginTop: 16 }}>
                  <InputField label="Book Again — Days Since Last Visit" value={settings.book_again_days || 30} onChange={v => u('book_again_days', v)} type="number" placeholder="30" />
                </div>
              )}

              <div style={{ ...S.sectionLabel, marginTop: 28 }}>Staff Notifications</div>
              <div style={S.switchGroup}>
                <SwitchRow label={`New ${terms.booking} Alert`} desc={`Email the ${terms.staff.toLowerCase()} when they get a new ${terms.booking.toLowerCase()}`} checked={settings.notify_staff_new_booking !== false} onChange={v => u('notify_staff_new_booking', v)} />
                <SwitchRow label="Daily Schedule Email" desc={`Send each ${terms.staff.toLowerCase()} their schedule every morning`} checked={settings.notify_staff_daily === true} onChange={v => u('notify_staff_daily', v)} />
              </div>

              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Notifications'}</button>
              </div>
            </div>
          )}

          {/* ── PAYMENTS ────────────────────────────────────────────── */}
          {tab === 'payments' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Payments & Billing</h2>
                <p style={S.cardDesc}>Connect Stripe to accept payments, configure tax rates, and manage tipping.</p>
              </div>
              <div style={S.divider} />

              <div style={S.stripeCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: stripeStatus?.connected ? '#065f46' : '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>💳</div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 15 }}>Stripe {stripeStatus?.connected ? 'Connected' : 'Not Connected'}</div>
                    <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{stripeStatus?.connected ? `Account: ${stripeStatus.account_id || 'Active'}` : 'Connect Stripe to accept online payments'}</div>
                  </div>
                </div>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: stripeStatus?.connected ? '#4ade80' : '#f59e0b', boxShadow: stripeStatus?.connected ? '0 0 8px #4ade80' : '0 0 8px #f59e0b' }} />
              </div>
              {!stripeStatus?.connected && (
                <button style={{ ...S.saveBtn, marginTop: 16 }} onClick={handleStripeConnect} disabled={saving}>
                  {saving ? 'Starting...' : '🔗 Connect Stripe Account'}
                </button>
              )}

              <div style={{ ...S.sectionLabel, marginTop: 28 }}>Tax & Pricing</div>
              <div style={S.grid2}>
                <InputField label="Tax Rate (%)" value={settings.tax_rate} onChange={v => u('tax_rate', v)} type="number" placeholder="8.375" />
                <SelectField label="Currency" value={settings.currency || 'usd'} onChange={v => u('currency', v)}
                  options={[{ value: 'usd', label: 'USD ($)' }, { value: 'cad', label: 'CAD (C$)' }, { value: 'gbp', label: 'GBP (£)' }, { value: 'eur', label: 'EUR (€)' }]} />
              </div>

              {features.tips && (
                <>
                  <div style={{ ...S.sectionLabel, marginTop: 24 }}>Tipping</div>
                  <div style={S.switchGroup}>
                    <SwitchRow label="Enable Tipping" desc={`Allow ${terms.customer.toLowerCase()}s to add a tip at checkout`} checked={settings.enable_tips !== false} onChange={v => u('enable_tips', v)} />
                  </div>
                  {settings.enable_tips !== false && (
                    <div style={{ marginTop: 12 }}>
                      <InputField label="Suggested Tip Percentages (comma-separated)" value={settings.tip_presets || '15,18,20,25'} onChange={v => u('tip_presets', v)} placeholder="15,18,20,25" />
                    </div>
                  )}
                </>
              )}

              <div style={S.cardFooter}>
                <button style={S.saveBtn} onClick={saveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Payment Settings'}</button>
              </div>
            </div>
          )}

          {/* ── ACCOUNT & SECURITY ──────────────────────────────────── */}
          {tab === 'account' && (
            <div style={S.card}>
              <div style={S.cardHeader}>
                <h2 style={S.cardTitle}>Account & Security</h2>
                <p style={S.cardDesc}>Manage your account credentials and security settings.</p>
              </div>
              <div style={S.divider} />

              <div style={S.sectionLabel}>Change Password</div>
              <div style={{ maxWidth: 400 }}>
                <InputField label="Current Password" value={passwordForm.current} onChange={v => setPasswordForm(p => ({ ...p, current: v }))} type="password" placeholder="Enter current password" />
                <InputField label="New Password" value={passwordForm.new} onChange={v => setPasswordForm(p => ({ ...p, new: v }))} type="password" placeholder="Min 8 characters" />
                <InputField label="Confirm New Password" value={passwordForm.confirm} onChange={v => setPasswordForm(p => ({ ...p, confirm: v }))} type="password" placeholder="Re-enter new password" />
                {passwordError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 4, marginBottom: 8 }}>{passwordError}</div>}
                <button style={{ ...S.saveBtn, marginTop: 8 }} onClick={handlePasswordChange} disabled={saving || !passwordForm.current || !passwordForm.new}>
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>

              <div style={{ ...S.sectionLabel, marginTop: 36 }}>Your Plan</div>
              <div style={S.planCard}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 18, color: '#f8fafc' }}>
                    {settings.plan === 'pro' ? '⚡ Pro Plan' : settings.plan === 'founding' ? '🌟 Founding Member' : '🆓 Free Trial'}
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
                    {settings.plan === 'founding' ? '$99/mo — locked in forever' : settings.plan === 'pro' ? '$149/mo' : 'Free for 14 days'}
                  </div>
                </div>
                <button style={S.planBtn}>Manage Plan</button>
              </div>

              <div style={{ ...S.sectionLabel, marginTop: 36, color: '#ef4444' }}>Danger Zone</div>
              <div style={S.dangerCard}>
                <div>
                  <div style={{ fontWeight: 600, color: '#fca5a5' }}>Delete Account</div>
                  <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>Permanently delete your business and all associated data. This cannot be undone.</div>
                </div>
                <button style={S.dangerBtn} onClick={() => setShowDeleteConfirm(true)}>Delete Account</button>
              </div>
              {showDeleteConfirm && (
                <div style={S.dangerConfirm}>
                  <p style={{ color: '#fca5a5', fontWeight: 600 }}>⚠️ Are you absolutely sure?</p>
                  <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>This will permanently delete all customers, bookings, staff, and business data. Type your business name to confirm:</p>
                  <input style={{ ...S.input, marginTop: 10, borderColor: '#7f1d1d' }} placeholder={settings.business_name || 'Business name'} id="delete-confirm-input" />
                  <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                    <button style={S.dangerBtn} onClick={() => {
                      const val = document.getElementById('delete-confirm-input')?.value;
                      if (val === settings.business_name) { flash('Account deletion requested. Contact support.'); setShowDeleteConfirm(false); }
                      else flash('Business name does not match');
                    }}>Permanently Delete</button>
                    <button style={S.uploadBtn} onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type="time"]::-webkit-calendar-picker-indicator { filter: invert(0.7); }
      `}</style>
    </div>
  );
}

// ── Subcomponents ───────────────────────────────────────────────────────────
function InputField({ label, value, onChange, type = 'text', placeholder, span2 }) {
  return (
    <div style={{ ...S.fieldGroup, ...(span2 ? { gridColumn: '1 / -1' } : {}) }}>
      <label style={S.label}>{label}</label>
      <input type={type} style={S.input} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div style={S.fieldGroup}>
      <label style={S.label}>{label}</label>
      <select style={S.select} value={value || ''} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div style={S.fieldGroup}>
      <label style={S.label}>{label}</label>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <input type="color" value={value || '#ff6b2b'} onChange={e => onChange(e.target.value)} style={S.colorPicker} />
        <input type="text" value={value || '#ff6b2b'} onChange={e => onChange(e.target.value)} style={{ ...S.input, flex: 1, fontFamily: 'monospace', fontSize: 13 }} />
      </div>
    </div>
  );
}

function SwitchRow({ label, desc, checked, onChange }) {
  return (
    <div style={S.switchRow}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, color: '#e2e8f0', fontSize: 14 }}>{label}</div>
        {desc && <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{desc}</div>}
      </div>
      <button onClick={() => onChange(!checked)}
        style={{ ...S.switch, background: checked ? '#059669' : '#334155', justifyContent: checked ? 'flex-end' : 'flex-start' }}>
        <div style={S.switchKnob} />
      </button>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────
const S = {
  page: { padding: '24px 32px 60px', color: '#e2e8f0', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' },
  spinner: { width: 36, height: 36, border: '3px solid #1e293b', borderTopColor: '#ff6b2b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  header: { marginBottom: 28 },
  title: { fontSize: 26, fontWeight: 700, color: '#f8fafc', margin: 0, letterSpacing: '-0.02em' },
  subtitle: { fontSize: 14, color: '#64748b', margin: '6px 0 0' },
  layout: { display: 'flex', gap: 24, alignItems: 'flex-start' },
  sidebar: { width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 24, background: '#111520', borderRadius: 12, padding: 6, border: '1px solid #1e293b' },
  sideTab: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: 'none', borderLeft: '3px solid transparent', background: 'transparent', color: '#94a3b8', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left', width: '100%', transition: 'all 0.15s ease' },
  content: { flex: 1, minWidth: 0 },
  card: { background: '#111520', borderRadius: 14, border: '1px solid #1e293b', padding: 28, marginBottom: 20 },
  cardHeader: { marginBottom: 0 },
  cardTitle: { fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  cardDesc: { fontSize: 13, color: '#64748b', margin: '6px 0 0', lineHeight: 1.5 },
  divider: { height: 1, background: '#1e293b', margin: '20px 0' },
  cardFooter: { marginTop: 28, paddingTop: 20, borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'flex-end' },
  fieldGroup: { marginBottom: 16 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: { width: '100%', padding: '10px 14px', background: '#0b0e14', border: '1px solid #2a2f3a', borderRadius: 8, color: '#e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', background: '#0b0e14', border: '1px solid #2a2f3a', borderRadius: 8, color: '#e2e8f0', fontSize: 14, outline: 'none', cursor: 'pointer', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', background: '#0b0e14', border: '1px solid #2a2f3a', borderRadius: 8, color: '#e2e8f0', fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, boxSizing: 'border-box' },
  colorPicker: { width: 44, height: 44, border: '2px solid #2a2f3a', borderRadius: 10, cursor: 'pointer', padding: 2, background: '#0b0e14' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px' },
  switchGroup: { display: 'flex', flexDirection: 'column', gap: 0 },
  switchRow: { display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid #1a1f2e' },
  switch: { width: 44, height: 24, borderRadius: 12, padding: 2, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0, transition: 'all 0.2s ease' },
  switchKnob: { width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  hourRow: { display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid #1a1f2e', transition: 'opacity 0.2s ease' },
  hourDay: { width: 100, flexShrink: 0 },
  hourTimes: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  timeInput: { padding: '8px 12px', background: '#0b0e14', border: '1px solid #2a2f3a', borderRadius: 8, color: '#e2e8f0', fontSize: 14, outline: 'none' },
  toggleBtn: { padding: '6px 14px', border: '1px solid #2a2f3a', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  industryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 },
  industryCard: { padding: '20px 16px', border: '2px solid #2a2f3a', borderRadius: 12, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s ease', position: 'relative', background: '#13161c' },
  selectedBadge: { position: 'absolute', top: 8, right: 8, fontSize: 10, fontWeight: 700, color: '#fff', padding: '2px 8px', borderRadius: 4 },
  previewSection: { background: '#0b0e14', borderRadius: 10, padding: 20, border: '1px solid #1e293b' },
  previewTitle: { fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 },
  featureGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  featureChip: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#111520', borderRadius: 6, fontSize: 13, color: '#cbd5e1', border: '1px solid #1e293b' },
  termGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 },
  termItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  termKey: { color: '#64748b', textTransform: 'capitalize', fontWeight: 500 },
  termArrow: { color: '#475569' },
  termVal: { color: '#e2e8f0', fontWeight: 600 },
  logoSection: { display: 'flex', gap: 20, alignItems: 'center' },
  logoPreview: { width: 96, height: 96, borderRadius: 12, background: '#0b0e14', border: '2px dashed #2a2f3a', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 },
  uploadBtn: { padding: '8px 16px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  stripeCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 20, background: '#0b0e14', borderRadius: 12, border: '1px solid #1e293b' },
  planCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 20, background: '#0b0e14', borderRadius: 12, border: '1px solid #1e293b' },
  planBtn: { padding: '8px 20px', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  dangerCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 20, background: '#0b0e14', borderRadius: 12, border: '1px solid #7f1d1d' },
  dangerBtn: { padding: '8px 18px', background: '#7f1d1d', border: '1px solid #991b1b', borderRadius: 8, color: '#fca5a5', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  dangerConfirm: { marginTop: 16, padding: 20, background: '#1a0505', borderRadius: 12, border: '1px solid #7f1d1d' },
  saveBtn: { padding: '10px 28px', background: '#ff6b2b', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  toast: { position: 'fixed', top: 20, right: 20, background: '#1e293b', border: '1px solid #334155', padding: '12px 20px', borderRadius: 10, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 500, color: '#e2e8f0', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' },
};

export default BusinessSettings;
